import { useEffect, useState } from 'react'
import { useSnackbar } from '@/contexts/SnackbarContext'
import { SnackbarType } from '@/utils/snackbar'
import { getUserErrorMessage } from '@/utils/get-user-error-message'
import { sendSmsToDrivers } from '../services/boxTruckLoadsApi'
import { BoxTruckSmsSurface, smsFailureReason, trackBoxTruckSmsSendFailed, trackBoxTruckSmsSent } from '../utils/analytics'

/** The conversation a composer is writing into. `line` is null before anyone has written. */
interface SmsComposerThread {
    /** Null for a number nobody on the roster answers to — there is no driver to send to. */
    driverAccountId: string | null
    counterpart: string
    line: string | null
}

/**
 * Typing a reply and sending it — the part the SMS page and the load drawer must not each own.
 *
 * Both surfaces compose into the same vendor with the same four outcomes, and when they each had a
 * copy the copies drifted within a day: one classified a thrown error by HTTP status and the other
 * filed everything as `server`, one isolated the post-send refresh so a failed re-read was not
 * reported as a failed send and the other did not. Neither difference was a decision.
 *
 * What stays with the caller is what genuinely differs: WHICH thread this is, whether it is still on
 * screen when an answer comes back, and what else the surface has to refresh afterwards.
 *
 * The one outcome worth reading twice is `unconfirmed`: the call timed out or Quo answered in a way
 * the server could not read, so the message MAY have gone out. Reported as a failure, the operator
 * sends it again and the driver reads the same words twice on the company's bill — so the draft is
 * cleared and the thread re-read instead, where the stored row says `unconfirmed` honestly.
 */
export function useSmsComposer({
    surface,
    mine,
    thread,
    threadKey,
    stillOpen,
    refresh,
    onSent,
}: {
    surface: BoxTruckSmsSurface
    /** Whether this is the caller's OWN conversation, where the surface can tell. Reported with the
     *  send, so answering from a colleague's thread is countable rather than merely argued about. */
    mine?: boolean
    thread: SmsComposerThread | null
    /**
     * What makes this a DIFFERENT conversation, in the surface's own terms — the page keys by line and
     * number, the drawer by driver. Changing it clears the draft, so half a message typed to one
     * driver cannot be sent to the next one opened.
     *
     * Deliberately the caller's to define rather than derived from `thread`: the drawer's line is
     * filled in by the first arriving message, and treating that as a new conversation would wipe a
     * reply mid-sentence.
     */
    threadKey: string | null
    /** Is the thread this send started in still the one on screen? Guards clearing someone else's draft. */
    stillOpen: () => boolean
    /** Re-read the thread. Owns its own error handling: a failed refresh is not a failed send. */
    refresh: () => Promise<void>
    /** Anything else the surface shows about a sent message — the conversation list's last line, say. */
    onSent?: (text: string) => void
}): { draft: string; setDraft: (text: string) => void; sending: boolean; send: () => Promise<void> } {
    const { showSnackbar } = useSnackbar()
    const [draft, setDraft] = useState('')
    const [sending, setSending] = useState(false)

    useEffect(() => setDraft(''), [threadKey])

    const send = async () => {
        const text = draft.trim()
        // Enter calls this directly, so a fast double press would send the same words twice — the
        // disabled button is not the guard here, this is.
        if (sending || !text || !thread) return
        if (!thread.driverAccountId) {
            showSnackbar(SnackbarType.ERROR, 'This number is not one of your drivers, so Numeo cannot text it back')
            return
        }
        setSending(true)
        try {
            // Which conversation this answers, when there is one. The server takes the sending line
            // from the thread itself, so the answer cannot leave on a number other than the one it is
            // on; without a thread it picks the line, which is how a first message works.
            const result = await sendSmsToDrivers(
                [thread.driverAccountId],
                text,
                thread.line ? { counterpart: thread.counterpart, line: thread.line } : undefined
            )

            const unconfirmed = result.unconfirmed?.[0]?.message
            if (!result.sent && unconfirmed) {
                trackBoxTruckSmsSent({ surface, recipients: 1, outcome: 'unconfirmed', mine })
                if (stillOpen()) setDraft('')
                showSnackbar(SnackbarType.WARNING, `${unconfirmed} — check the conversation before sending it again`)
                await refresh()
                return
            }
            if (!result.sent) {
                // Quo's own refusal, which the server already phrased. Carried as the reason rather
                // than a bare Error so the analytics bucket is the vendor's answer, not "server".
                const refusal = result.failed[0]?.message
                trackBoxTruckSmsSendFailed(surface, refusal ? 'quo_refused' : 'server')
                showSnackbar(SnackbarType.ERROR, refusal || 'The message was not sent')
                return
            }
            trackBoxTruckSmsSent({ surface, recipients: 1, outcome: 'sent', mine })
            if (stillOpen()) setDraft('')
            onSent?.(text)
            await refresh()
        } catch (e) {
            // By STATUS, not by reading the sentence shown to the operator: "rejected the API key",
            // "is rate-limiting this workspace" and "refused the request" all contain "Quo".
            trackBoxTruckSmsSendFailed(surface, smsFailureReason(e))
            showSnackbar(SnackbarType.ERROR, getUserErrorMessage(e) || 'Could not send the message')
        } finally {
            setSending(false)
        }
    }

    return { draft, setDraft, sending, send }
}
